import stringify from 'fast-safe-stringify'

export type ApiResponse<T, Err> = {
    response: Response
    data?: T
    error?: Err
}

export function baseErrorFormatter<Err>(response: Response, error: Err): string {
    return `Error for URL: ${response.url} with status: ${response.status}: ${stringify(error)}`
}

export type HandleApiRequest<T, Err> = {
    apiCall: () => Promise<ApiResponse<T, Err>>
    options?: {
        errorFormatter?: (response: Response, error?: Err) => string
    }
}

// Wrapper function
export async function handleApiRequest<T, Err>({ apiCall, options = {} }: HandleApiRequest<T, Err>): Promise<T | undefined> {
    try {
        const { response, data, error } = await apiCall()
        if (error) {
            throw new Error(options.errorFormatter ? options.errorFormatter(response, error) : baseErrorFormatter(response, error))
        }
        if (!response.ok) {
            throw new Error(options.errorFormatter ? options.errorFormatter(response, error) : baseErrorFormatter(response, error))
        }

        return data
    } catch (error: unknown) {
        // Let AbortError propagate without wrapping - React Query handles it
        const isAbortError =
            (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') ||
            (error instanceof Error && error.name === 'AbortError')

        if (isAbortError || error instanceof Error) {
            throw error
        }
        throw new Error('An unexpected error occurred when fetching data')
    }
}
